import "@/App.css";
import { useState, useCallback } from "react";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Toaster } from "@/components/ui/sonner";
import { HostnameGenerator } from "@/components/HostnameGenerator";
import { HostInventory } from "@/components/HostInventory";
import { HostDetail } from "@/components/HostDetail";
import { APP } from "@/constants/testIds";
import {
  Cube,
  Sparkle,
  Table as TableIcon,
  X,
  Desktop,
} from "@phosphor-icons/react";

function App() {
  const [tab, setTab] = useState("inventory");
  const [openHosts, setOpenHosts] = useState([]); // array of host summaries

  const openHostTab = useCallback((host) => {
    setOpenHosts((prev) => {
      if (prev.some((h) => h.id === host.id)) return prev;
      return [
        ...prev,
        { id: host.id, hostname: host.hostname, host_type: host.host_type },
      ];
    });
    setTab(`host:${host.id}`);
  }, []);

  const closeHostTab = useCallback(
    (id) => {
      setOpenHosts((prev) => prev.filter((h) => h.id !== id));
      setTab((current) => (current === `host:${id}` ? "inventory" : current));
    },
    []
  );

  return (
    <div className="App min-h-screen">
      {/* Top nav */}
      <header className="bg-white border-b border-[#E5E7EB]">
        <div className="mx-auto max-w-[1600px] px-6 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="h-9 w-9 grid place-items-center bg-[#0052CC] text-white">
              <Cube size={20} weight="bold" />
            </div>
            <div>
              <div className="font-heading font-black text-[15px] tracking-tight leading-none">
                MEDOPS <span className="text-[#0052CC]">CONSOLE</span>
              </div>
              <div className="text-[11px] text-[#6B7280] font-mono-tech mt-0.5">
                internal / infrastructure ops
              </div>
            </div>
          </div>
          <div className="hidden sm:flex items-center gap-4 text-[11px] font-mono-tech text-[#6B7280]">
            <span className="flex items-center gap-1.5">
              <span className="inline-block h-1.5 w-1.5 rounded-full bg-[#059669]" />
              ONLINE
            </span>
            <span>v0.2.0 · PoC</span>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-[1600px] px-6 py-6">
        <Tabs value={tab} onValueChange={setTab} className="w-full">
          <TabsList className="bg-transparent p-0 h-auto gap-0 border-b border-[#E5E7EB] w-full justify-start rounded-none overflow-x-auto tech-scroll">
            <TabsTrigger
              data-testid={APP.tabGenerator}
              value="generator"
              className="rounded-none border-b-2 border-transparent data-[state=active]:border-[#0052CC] data-[state=active]:bg-transparent data-[state=active]:shadow-none data-[state=active]:text-[#0052CC] px-5 py-3 font-heading font-bold text-sm uppercase tracking-wider text-[#4B5563] shrink-0"
            >
              <Sparkle size={16} weight="bold" className="mr-2" />
              Hostname Generator
            </TabsTrigger>
            <TabsTrigger
              data-testid={APP.tabInventory}
              value="inventory"
              className="rounded-none border-b-2 border-transparent data-[state=active]:border-[#0052CC] data-[state=active]:bg-transparent data-[state=active]:shadow-none data-[state=active]:text-[#0052CC] px-5 py-3 font-heading font-bold text-sm uppercase tracking-wider text-[#4B5563] shrink-0"
            >
              <TableIcon size={16} weight="bold" className="mr-2" />
              Host Inventory
            </TabsTrigger>

            {openHosts.map((h) => (
              <TabsTrigger
                key={h.id}
                data-testid={APP.tabHost(h.id)}
                value={`host:${h.id}`}
                className="rounded-none border-b-2 border-transparent data-[state=active]:border-[#0052CC] data-[state=active]:bg-transparent data-[state=active]:shadow-none data-[state=active]:text-[#0052CC] pl-4 pr-2 py-3 font-heading font-bold text-[13px] uppercase tracking-wider text-[#4B5563] shrink-0 group"
              >
                <Desktop size={14} weight="bold" className="mr-2 opacity-70" />
                <span className="font-mono-tech normal-case tracking-normal text-[12px]">
                  {h.hostname}
                </span>
                <span
                  role="button"
                  tabIndex={0}
                  data-testid={APP.tabHostClose(h.id)}
                  onClick={(e) => {
                    e.stopPropagation();
                    closeHostTab(h.id);
                  }}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();
                      e.stopPropagation();
                      closeHostTab(h.id);
                    }
                  }}
                  className="ml-2 h-5 w-5 grid place-items-center rounded-sm text-[#9CA3AF] hover:text-white hover:bg-[#111827] transition-colors cursor-pointer"
                  aria-label={`Close ${h.hostname} tab`}
                >
                  <X size={12} weight="bold" />
                </span>
              </TabsTrigger>
            ))}
          </TabsList>

          <TabsContent value="generator" className="mt-6 rise-in">
            <HostnameGenerator />
          </TabsContent>

          <TabsContent value="inventory" className="mt-6 rise-in">
            <HostInventory onOpenHost={openHostTab} />
          </TabsContent>

          {openHosts.map((h) => (
            <TabsContent
              key={h.id}
              value={`host:${h.id}`}
              className="mt-6 rise-in"
            >
              <HostDetail
                hostId={h.id}
                onBackToInventory={() => setTab("inventory")}
                onOpenHost={openHostTab}
              />
            </TabsContent>
          ))}
        </Tabs>
      </main>

      <Toaster position="bottom-right" richColors closeButton />
    </div>
  );
}

export default App;
